import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Box } from 'atoms';
import {
	PageContainer,
	HeaderContainer,
	HeaderRightSection,
	Title,
	Subtitle,
	StepsContainer,
	CustomSteps,
	StepItem,
	StepCircle,
	StepLabel,
	StepConnector,
	BackButton,
	EditButton,
	VerifyButton,
} from 'componentsv2/forms/client/clientFormStyles';
import {
	ProjectInformationFormV2,
	NodeInformationFormV2,
	WarehouseInfoFormV2,
} from 'componentsv2/forms/project';
import {
	PROJECT_TYPE_TRANSPORTATION,
	isWarehousingProject,
} from 'componentsv2/forms/project/projectTypes';
import {
	crossDockChargeList,
	crossDockRateFrom,
	REVENUE_MODEL_COST_PLUS,
} from 'componentsv2/forms/project/warehouseRevenue';
import { getWarehouseProjectV2, updateWarehouseRevenueModelV2 } from 'store/warehousingSliceV2';
import {
	getProjectByIdV2,
	createProjectV2,
	updateProjectV2,
	verifyProjectV2,
} from 'store/projectSliceV2';
import { searchNodes } from 'store/nodeSliceV2';

const PROJECT_LIST_ROUTE = '/projects';

export default function CreateProjectPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	// Determine if we are in edit mode (id exists in URL)
	const isEditMode = Boolean(id);

	// View/Edit mode states
	const [isViewMode, setIsViewMode] = useState(isEditMode);

	// Step state
	const [currentStep, setCurrentStep] = useState(0);

	// Initialize data state (will be populated from API if in edit mode)
	const [projectInfoData, setProjectInfoData] = useState({});
	const [nodes, setNodes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [savedProjectId, setSavedProjectId] = useState(null);
	const [nodesSaved, setNodesSaved] = useState(false);
	// Revenue model for warehousing projects (step 2)
	const [warehouseData, setWarehouseData] = useState({});

	// Step 1's live selection. Kept out of projectInfoData because that feeds Formik's
	// initialValues with enableReinitialize — writing to it mid-edit would reset the form.
	const [draftProjectType, setDraftProjectType] = useState(null);

	const isWarehousing = isWarehousingProject(draftProjectType ?? projectInfoData.project_type);

	/**
	 * POD and adhoc-trip approval are trip settings, so Warehousing leaves them blank.
	 * Left out of the payload entirely for that type — the backend applies its own
	 * defaults; forcing `true` here would switch on an approval gate that can never fire.
	 */
	const resolveAdhocApproval = (formData) => {
		if (isWarehousingProject(formData.project_type)) return undefined;
		if (formData.adhoc_approval_required === undefined) return true;
		return (
			formData.adhoc_approval_required === 'true' || formData.adhoc_approval_required === true
		);
	};

	// Helper: Build base API data structure
	const buildBaseApiData = (formData) => ({
		project_name: formData.project_name,
		project_code: formData.project_name,
		client_id: formData.client_id,
		project_address: formData.project_address,
		latitude: formData.latitude,
		longitude: formData.longitude,
		pin_code: formData.pincode,
		zone: formData.zone || undefined,
		city: formData.city || undefined,
		state: formData.state || undefined,
		business_unit_id: formData.business_unit_id,
		project_category: formData.project_category || 'regular',
		project_type: formData.project_type || PROJECT_TYPE_TRANSPORTATION,
		gst_number: formData.gst_number || undefined,
		pod_required: formData.pod_required,
		cost_center_id: formData.cost_center_id ? formData.cost_center_id : undefined,
		temperature_control: formData.temperature_control || false,
		adhoc_approval_required: resolveAdhocApproval(formData),
	});

	// Helper: Get backend node IDs (filter out temporary client-side IDs)
	const getBackendNodeIds = () => {
		const backendNodes = nodes.filter((node) => node.id && node.id < 1000000000000);
		return backendNodes.length > 0 ? backendNodes.map((node) => node.id) : null;
	};

	// Helper: Remove undefined values from object
	const removeUndefinedValues = (obj) => {
		Object.keys(obj).forEach((key) => {
			if (obj[key] === undefined) {
				delete obj[key];
			}
		});
		return obj;
	};

	// Helper function to prepare API data from form data
	const prepareApiData = (formData) => {
		const apiData = buildBaseApiData(formData);

		// In edit mode, include nodes if they have backend IDs
		if (isEditMode) {
			// project_type is immutable server-side and the project search response does
			// not return it, so our value here is only ever a default — sending it would
			// trip the backend's "Project type cannot be changed" guard on a warehousing
			// project and block every save.
			delete apiData.project_type;
			// project_code is immutable too. It is only seeded from the name at create time,
			// so after a rename the two differ and sending it fails the backend's guard.
			delete apiData.project_code;

			const nodeIds = getBackendNodeIds();
			if (nodeIds) {
				apiData.nodes = nodeIds;
			}
			// Include is_active if provided
			if (formData.is_active !== undefined) {
				apiData.is_active = formData.is_active;
			}
		}

		return removeUndefinedValues(apiData);
	};

	// Helper: Format project info data from API result
	const formatProjectInfoData = (result) => ({
		project_name: result.project_name,
		project_code: result.project_code,
		project_address: result.project_address,
		pincode: result.pin_code || result.pincode,
		zone: result.zone,
		city: result.city,
		state: result.state,
		client_id: result.client_id,
		latitude: result.latitude,
		longitude: result.longitude,
		business_unit_id: result.business_unit_id,
		project_category: result.project_category,
		pod_required: result.pod_required,
		gst_number: result.gst_number,
		cost_center_id: result.cost_center_id,
		project_type: result.project_type || PROJECT_TYPE_TRANSPORTATION,
		temperature_control: result.temperature_control,
		adhoc_approval_required: result.adhoc_approval_required,
		is_active: result.is_active,
	});

	// Helper: Format nodes from search API result
	const formatNodesFromSearchApi = (nodesArray, projectName) => {
		if (!nodesArray || !Array.isArray(nodesArray)) return [];

		return nodesArray.map((node) => ({
			id: node.id || node.node_id,
			project_name: projectName || '',
			node_type: node.node_type,
			node_name: node.node_name,
			node_address: node.node_address,
			latitude: node.latitude,
			longitude: node.longitude,
			poc_name: node.poc_name || '',
			poc_number: node.poc_mobile_number || node.poc_number || '',
			pincode: node.pin_code || node.pincode,
			zone: node.zone || '',
			city: node.city || '',
			state: node.state || '',
		}));
	};

	// Fetch nodes using search API
	const fetchNodesByProjectId = async (projectId, projectName) => {
		if (!projectId) return [];

		try {
			const response = await dispatch(
				searchNodes({
					filter_by: { project_id: Number(projectId) },
					pagination: { page: 1, per_page: 1000 }, // Fetch all nodes
				})
			);

			// Extract nodes from response - handle different response structures
			let nodesData = [];
			if (response?.data?.nodes) {
				nodesData = response.data.nodes;
			} else if (Array.isArray(response?.data)) {
				nodesData = response.data;
			} else if (Array.isArray(response)) {
				nodesData = response;
			}

			console.log('Fetched nodes from search API:', nodesData);
			return formatNodesFromSearchApi(nodesData, projectName);
		} catch (error) {
			console.error('Error fetching nodes:', error);
			return [];
		}
	};

	// Helper: Check if project is verified
	const checkProjectVerification = (result) =>
		result.verified === true ||
		result.is_verified === true ||
		result.status === 'verified' ||
		result.verification_status === 'verified' ||
		false;

	// Helper: Check if all nodes have backend IDs
	const checkAllNodesHaveBackendIds = (nodesArray) => {
		if (nodesArray.length === 0) return false;
		return nodesArray.every((node) => node.id && node.id < 1000000000000);
	};

	// Revenue model (+ its additional charges, a sibling field, not nested inside it)
	// for an existing warehousing project. Only the warehousing dashboard returns
	// either — the project search response includes neither.
	const fetchRevenueModel = async (warehouseProjectId) => {
		try {
			const result = await dispatch(
				getWarehouseProjectV2({
					// Display id ('IRPL-86') on purpose: Number() on it is NaN. The backend
					// coerces project_id from the IRPL form (app.py -> normalize_v2_ids).
					project_id: warehouseProjectId,
					showLoading: false,
				})
			);
			if (!result?.revenue_model) return null;
			return { ...result.revenue_model, additional_charges: result.additional_charges || [] };
		} catch (error) {
			// Non-fatal: step 2 falls back to an empty form
			return null;
		}
	};

	// The dashboard's revenue_model shape -> step 2's form shape. Shared by the initial
	// load and the re-read after a save, so the two cannot drift.
	const applyRevenueModel = (revenueModel) => {
		if (!revenueModel) return;
		setWarehouseData({
			model_type: revenueModel.model_type,
			slabs: (revenueModel.slabs || []).map((slab) => ({
				slab_min: slab.slab_min,
				slab_max: slab.slab_max,
				rate_per_order: slab.rate_per_order,
			})),
			cost_plus_margin_pct: revenueModel.cost_plus_margin_pct ?? '',
			cross_dock_rate: crossDockRateFrom(revenueModel.additional_charges),
		});
	};

	// Helper: Process fetched project data
	const processFetchedProjectData = async (result) => {
		setProjectInfoData(formatProjectInfoData(result));

		// The project search response carries no revenue model, so step 2 has nothing to
		// hydrate from on its own. The warehousing project dashboard is the only endpoint
		// that returns it — fetch from there when this is a warehousing project.
		if (isWarehousingProject(result.project_type)) {
			applyRevenueModel(await fetchRevenueModel(result.id));
		}

		// Set savedProjectId since project already exists in edit mode
		if (result.id) {
			setSavedProjectId(result.id);
		}

		// Fetch nodes using search API
		const fetchedNodes = await fetchNodesByProjectId(result.id, result.project_name);
		setNodes(fetchedNodes);

		// Set nodesSaved to true if all nodes have backend IDs (were saved)
		if (checkAllNodesHaveBackendIds(fetchedNodes)) {
			setNodesSaved(true);
		}

		// Check verification status
		const isProjectVerified = checkProjectVerification(result);
		console.log('Project verification status:', {
			verified: result.verified,
			is_verified: result.is_verified,
			status: result.status,
			verification_status: result.verification_status,
			isProjectVerified,
		});
		setIsVerified(isProjectVerified);
	};

	// Fetch project data if in edit mode
	useEffect(() => {
		if (!isEditMode || !id) return;

		const fetchProjectData = async () => {
			setLoading(true);
			try {
				const result = await dispatch(getProjectByIdV2({ id }));
				if (result) {
					await processFetchedProjectData(result);
				}
			} catch (error) {
				// Error handled silently or by the action
			} finally {
				setLoading(false);
			}
		};

		fetchProjectData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isEditMode]);

	// Reset nodesSaved when nodes array changes (node added/edited/deleted)
	useEffect(() => {
		if (!isEditMode && nodes.length > 0) {
			// Check if all nodes have backend IDs (were saved)
			const allNodesHaveBackendIds = nodes.every(
				(node) => node.id && node.id < 1000000000000
			);
			// If nodes were modified (some don't have backend IDs), reset nodesSaved
			if (!allNodesHaveBackendIds) {
				setNodesSaved(false);
			}
		}
	}, [nodes, isEditMode]);

	// Handle Edit button click
	const handleEditClick = () => {
		setIsViewMode(false);
	};

	// Helper: Format verification error from response (for non-422 errors)
	const formatResponseError = (errorMessage) => {
		if (!errorMessage.includes("'message'") || !errorMessage.includes("'errors'")) {
			return errorMessage;
		}

		try {
			const jsonString = errorMessage
				.replace(/'/g, '"')
				.replace(/True/g, 'true')
				.replace(/False/g, 'false')
				.replace(/None/g, 'null');
			const parsed = JSON.parse(jsonString);
			let formattedError = parsed.message || 'Project verification failed.';

			if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
				const errorDetails = parsed.errors
					.map((err) => `• ${err.message || err.field || 'Unknown error'}`)
					.join('\n');
				formattedError = `${formattedError}\n\n${errorDetails}`;
			}

			return formattedError;
		} catch (parseError) {
			console.error('Error parsing verification error:', parseError);
			return errorMessage;
		}
	};

	// Handle Verify button click
	const handleVerifyClick = async () => {
		if (!id) return;

		setLoading(true);
		try {
			const response = await dispatch(verifyProjectV2({ id }));
			console.log('Verify API response:', response);

			// Check if response indicates an error (even with 200 status)
			if (response?.error || response?.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || 'Failed to verify project';

				const formattedError = formatResponseError(errorMessage);
				message.error({
					content: formattedError,
					duration: 10, // Show for 10 seconds
					style: { whiteSpace: 'pre-line' }, // Preserve line breaks
				});
				return;
			}

			// Update verification status immediately from response
			if (response?.data?.verified === true || response?.data?.status === 'verified') {
				setIsVerified(true);
			} else {
				// Fallback: reload project data to reflect verification status
				const result = await dispatch(getProjectByIdV2({ id }));
				if (result) {
					const isProjectVerified =
						result.verified === true ||
						result.is_verified === true ||
						result.status === 'verified' ||
						result.verification_status === 'verified' ||
						false;
					console.log('Reloaded project verification status:', isProjectVerified);
					setIsVerified(isProjectVerified);
				}
			}
		} catch (error) {
			// Error is already formatted and displayed by handle422Error in API layer
			// No need to show it again here
		} finally {
			setLoading(false);
		}
	};

	// Handle Cancel button click
	const handleCancel = () => {
		if (isEditMode) {
			setIsViewMode(true);
		} else {
			navigate(PROJECT_LIST_ROUTE);
		}
	};

	// Handle Save Project (creates/updates project but stays on page)
	const handleSaveProject = async (formData, setErrors) => {
		setLoading(true);
		try {
			const apiData = prepareApiData(formData);

			if (isEditMode) {
				// In edit mode, update the project
				await dispatch(updateProjectV2({ id, req_data: apiData, setErrors }));
				const refreshedProject = await dispatch(getProjectByIdV2({ id }));
				if (refreshedProject) {
					await processFetchedProjectData(refreshedProject);
				} else {
					setProjectInfoData((prev) => ({
						...prev,
						...formData,
						adhoc_approval_required:
							formData.adhoc_approval_required !== undefined
								? formData.adhoc_approval_required === true
								: prev.adhoc_approval_required,
					}));
				}
				setIsViewMode(true);
				setCurrentStep(0);
				message.success('Project information saved successfully!');
			} else if (!savedProjectId) {
				// In create mode, create the project if not already saved
				const result = await dispatch(createProjectV2({ req_data: apiData, setErrors }));

				if (result?.data?.id) {
					const projectId = result.data.id;
					setSavedProjectId(projectId);
					// Update projectInfoData with project_id
					setProjectInfoData((prev) => ({
						...prev,
						...formData,
						project_id: projectId,
					}));
					message.success('Project information saved successfully!');
				}
			} else {
				message.info('Project information already saved.');
			}
		} catch (error) {
			// Error handled by the action (toaster message shown)
		} finally {
			setLoading(false);
		}
	};

	// Warehousing projects are created in a single call — the revenue model is part of
	// the create payload and has no endpoint of its own (backend TDD §5.2 / §8).
	const buildWarehouseConfig = (values) => {
		const revenueModel =
			values.model_type === REVENUE_MODEL_COST_PLUS
				? {
						model_type: values.model_type,
						cost_plus_margin_pct: Number(values.cost_plus_margin_pct),
				  }
				: {
						model_type: values.model_type,
						// sort_order is derived from array order by the backend
						slabs: (values.slabs || []).map((slab) => ({
							slab_min: Number(slab.slab_min),
							slab_max: Number(slab.slab_max),
							rate_per_order: Number(slab.rate_per_order),
						})),
				  };

		const config = { revenue_model: revenueModel };

		// Sibling of revenue_model, not nested inside it — optional, so only sent when
		// at least one fully-filled-in charge row exists.
		// The form carries one Cross Dock rate; the API takes a generic charge list keyed
		// by basis, so it goes out as that list's single per_bag row (omitted when blank).
		const additionalCharges = crossDockChargeList(values.cross_dock_rate);
		if (additionalCharges.length) config.additional_charges = additionalCharges;

		return config;
	};

	const handleWarehouseSubmit = async (values, setErrors) => {
		setLoading(true);
		try {
			// Edit mode: only the revenue terms are saved here. Project information has its
			// own Save on step 1, and the terms have their own endpoint — project /update
			// does not accept warehouse_config.
			if (isEditMode) {
				const isCostPlus = values.model_type === REVENUE_MODEL_COST_PLUS;
				await dispatch(
					updateWarehouseRevenueModelV2({
						req_data: {
							// Display id — see the note in fetchRevenueModel above.
							project_id: id,
							reason: (values.reason || '').trim(),
							// model_type is locked in the form, but the API takes it and uses
							// it to interpret which terms below are meaningful.
							model_type: values.model_type,
							...(isCostPlus
								? { cost_plus_margin_pct: Number(values.cost_plus_margin_pct) }
								: {
										slabs: (values.slabs || []).map((slab) => ({
											slab_min: Number(slab.slab_min),
											slab_max: Number(slab.slab_max),
											rate_per_order: Number(slab.rate_per_order),
										})),
								  }),
							// Always sent, empty included: an empty list is how a project that
							// no longer bills Cross Dock is expressed.
							additional_charges: crossDockChargeList(values.cross_dock_rate),
						},
						setErrors,
					})
				);

				// Re-read rather than trusting what was submitted: the server sorts slabs
				// and is the authority on what was stored.
				applyRevenueModel(await fetchRevenueModel(id));
				setIsViewMode(true);
				return;
			}

			const apiData = {
				...prepareApiData(projectInfoData),
				warehouse_config: buildWarehouseConfig(values),
			};

			const result = await dispatch(createProjectV2({ req_data: apiData, setErrors }));

			// The warehousing create response nests the project under `data.project`
			// alongside the seeded catalogs, unlike the flat transportation response.
			const created = result?.data?.project || result?.data;
			if (created?.id) {
				const seededTypes = result?.data?.manpower_types?.length;
				const seededHeads = result?.data?.monthly_cost_heads?.length;
				if (seededTypes || seededHeads) {
					message.success(
						`Project created. ${seededTypes || 0} manpower types and ${
							seededHeads || 0
						} monthly cost heads are ready for entry.`
					);
				}
				navigate(`/warehousing/${created.id}`);
			}
		} catch (error) {
			// Error handled by the action (toaster message shown)
		} finally {
			setLoading(false);
		}
	};

	// Handle form submission (called from step 2 or directly from step 1 in edit mode)
	const handleSubmit = async (formData, setErrors) => {
		// If project was already saved, skip project create and just navigate
		if (!isEditMode && savedProjectId) {
			navigate(PROJECT_LIST_ROUTE);
			return;
		}

		setLoading(true);
		try {
			const apiData = prepareApiData(formData);

			const action = isEditMode
				? updateProjectV2({ id, req_data: apiData, setErrors })
				: createProjectV2({ req_data: apiData, setErrors });

			const result = await dispatch(action);

			if (result?.data?.id) {
				navigate(PROJECT_LIST_ROUTE);
			}
		} catch (error) {
			// Error handled by the action (toaster message shown)
		} finally {
			setLoading(false);
		}
	};

	if (loading && isEditMode) {
		return (
			<PageContainer>
				<Box>Loading...</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			{/* Header */}
			<HeaderContainer>
				<BackButton as={ArrowLeftOutlined} onClick={() => navigate(PROJECT_LIST_ROUTE)} />
				<Box>
					<Title>{isEditMode ? 'Project Information' : 'Create Project'}</Title>
					<Subtitle>
						{isEditMode
							? 'Edit project information'
							: 'Create project for Client Location'}
					</Subtitle>
				</Box>
				{isEditMode && isViewMode && (
					<HeaderRightSection>
						<VerifyButton
							onClick={handleVerifyClick}
							loading={loading}
							verified={isVerified}
							disabled={isVerified}
						>
							<CheckCircleOutlined />
							{isVerified ? 'Verified' : 'Verify'}
						</VerifyButton>
						<EditButton onClick={handleEditClick}>
							<EditOutlined />
							Edit
						</EditButton>
					</HeaderRightSection>
				)}
			</HeaderContainer>

			{/* Steps */}
			<StepsContainer>
				<CustomSteps>
					<StepItem>
						<StepCircle active={currentStep === 0} completed={currentStep > 0}>
							{currentStep > 0 ? '✓' : '1'}
						</StepCircle>
						<StepLabel active={currentStep === 0}>Project Information</StepLabel>
					</StepItem>
					<StepConnector completed={currentStep > 0} />
					<StepItem>
						<StepCircle active={currentStep === 1} completed={currentStep > 1}>
							2
						</StepCircle>
						<StepLabel active={currentStep === 1}>
							{isWarehousing ? 'Warehouse Info' : 'Node Information'}
						</StepLabel>
					</StepItem>
				</CustomSteps>
			</StepsContainer>

			{/* Step 1: Project Information */}
			{currentStep === 0 && (
				<ProjectInformationFormV2
					projectInfoData={projectInfoData}
					setProjectInfoData={setProjectInfoData}
					setCurrentStep={setCurrentStep}
					isEditMode={isEditMode}
					isViewMode={isViewMode}
					onSubmit={handleSubmit}
					onCancel={handleCancel}
					onSave={handleSaveProject}
					loading={loading}
					savedProjectId={savedProjectId}
					onProjectTypeChange={setDraftProjectType}
				/>
			)}

			{/* Step 2 (warehousing): Warehouse Info */}
			{currentStep === 1 && isWarehousing && (
				<WarehouseInfoFormV2
					warehouseData={warehouseData}
					setWarehouseData={setWarehouseData}
					setCurrentStep={setCurrentStep}
					isEditMode={isEditMode}
					isViewMode={isViewMode}
					onSubmit={handleWarehouseSubmit}
					onCancel={handleCancel}
					loading={loading}
				/>
			)}

			{/* Step 2 (transportation): Node Information */}
			{currentStep === 1 && !isWarehousing && (
				<NodeInformationFormV2
					projectInfoData={projectInfoData}
					nodes={nodes}
					setNodes={setNodes}
					setCurrentStep={setCurrentStep}
					isEditMode={isEditMode}
					isViewMode={isViewMode}
					onSubmit={handleSubmit}
					onCancel={handleCancel}
					loading={loading}
					savedProjectId={savedProjectId}
					nodesSaved={nodesSaved}
					setNodesSaved={setNodesSaved}
				/>
			)}
		</PageContainer>
	);
}
